import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Brand, Category, Highlight, Item, StoryLinkType } from '@erp/shared';
import { GripVertical, ImageOff, ImagePlus, Link2, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Skeleton } from '../../components/shadcn/index.js';
import { Badge, Button, type ComboboxOption, confirmDialog, EmptyState, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { flattenCategoryOptions } from '../items/itemHelpers.js';

import {
  addHighlightMedia,
  createHighlight,
  deleteHighlight,
  deleteHighlightMedia,
  fetchHighlightCoverObjectUrl,
  fetchHighlightMediaObjectUrl,
  listHighlights,
  reorderHighlightMedia,
  reorderHighlights,
  setHighlightCover,
  updateHighlight,
} from './api.js';
import { LinkTargetPicker } from './StoryEditor.js';
import { useObjectUrl } from './useMedia.js';

/**
 * Highlights — the shop's permanent shelves.
 *
 * They used to be built out of stories, which meant a shelf could not exist
 * until this week's posts did, and went dark when those posts were archived.
 * A highlight now owns its pictures. Nothing on this page mentions stories.
 *
 * Laid out as a GRID of covers rather than a stack of rows because that is what
 * the customer sees — a row of circles — and an admin screen shaped like the
 * result is one you can check at a glance. Everything a highlight needs is set
 * in ONE dialog, so creating a full shelf is a single pass instead of create →
 * find the card → upload → set the link.
 */
export function HighlightsPage(): JSX.Element {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const canManage = usePermission('stories.manage');
  const [highlights, setHighlights] = useState<Highlight[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editing, setEditing] = useState<Highlight | null | undefined>(undefined);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reload = async (): Promise<void> => { setHighlights(await listHighlights()); };
  useEffect(() => {
    void reload();
    void api().get<{ items: Item[] }>('/items', { params: { limit: 500 } }).then((r) => setItems(r.data.items)).catch(() => undefined);
    void api().get<{ items: Category[] }>('/categories').then((r) => setCategories(r.data.items)).catch(() => undefined);
    void api().get<{ items: Brand[] }>('/brands').then((r) => setBrands(r.data.items)).catch(() => undefined);
  }, []);

  const productOptions = useMemo<ComboboxOption[]>(
    () => items.filter((i) => i.isActive).map((i) => ({ value: i.id, label: `${i.code} — ${i.name}`, sublabel: i.barcode ?? undefined, keywords: `${i.code} ${i.name} ${i.barcode ?? ''}` })),
    [items],
  );
  const categoryOptions = useMemo<ComboboxOption[]>(() => flattenCategoryOptions(categories).map((c) => ({ value: c.id, label: c.label })), [categories]);
  const brandOptions = useMemo<ComboboxOption[]>(() => brands.map((b) => ({ value: b.id, label: b.title })), [brands]);

  const onReorder = async (e: DragEndEvent): Promise<void> => {
    const { active, over } = e;
    if (!highlights || !over || active.id === over.id) return;
    const ids = highlights.map((h) => h.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    const byId = new Map(highlights.map((h) => [h.id, h]));
    setHighlights(next.map((id) => byId.get(id)!)); // optimistic
    try { await reorderHighlights(next); } catch { toast.error(tc('errors:server')); }
    await reload();
  };

  if (!highlights) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('highlights.title', { defaultValue: 'Highlights' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('highlights.description', { defaultValue: 'Permanent shelves in your app — their own pictures, or a shortcut to a category.' })}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setEditing(null)}>
            <Plus className="h-4 w-4" aria-hidden /> {t('highlights.add', { defaultValue: 'New highlight' })}
          </Button>
        )}
      </header>

      {highlights.length === 0 ? (
        <EmptyState
          icon={<ImageOff className="h-8 w-8" aria-hidden />}
          title={t('highlights.empty', { defaultValue: 'No highlights yet' })}
          description={t('highlights.emptyDescription', { defaultValue: 'Add a title, drop in a few pictures, and it appears in your app.' })}
          action={canManage ? <Button onClick={() => setEditing(null)}><Plus className="h-4 w-4" aria-hidden /> {t('highlights.add', { defaultValue: 'New highlight' })}</Button> : undefined}
        />
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onReorder(e)}>
            <SortableContext items={highlights.map((h) => h.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {highlights.map((h) => (
                  <HighlightTile key={h.id} highlight={h} canManage={canManage} onEdit={() => setEditing(h)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {canManage && highlights.length > 1 && (
            <p className="text-xs text-fg-muted">{t('highlights.reorderHint', { defaultValue: 'Drag to set the order they appear in the app.' })}</p>
          )}
        </>
      )}

      {editing !== undefined && (
        <HighlightDialog
          highlight={editing}
          productOptions={productOptions}
          categoryOptions={categoryOptions}
          brandOptions={brandOptions}
          onClose={() => setEditing(undefined)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

/** One cover in the grid — what the customer will see, plus its state. */
function HighlightTile({ highlight: h, canManage, onEdit }: { highlight: Highlight; canManage: boolean; onEdit: () => void }): JSX.Element {
  const { t } = useTranslation('stories');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: h.id, disabled: !canManage });
  const coverUrl = useObjectUrl(h.coverAttachmentId ? () => fetchHighlightCoverObjectUrl(h.id) : null, h.coverAttachmentId ?? 'none', 0);
  // Nothing to play and nowhere to go — the storefront hides it, so say so here
  // rather than let the shop wonder why it never appeared.
  const isEmpty = h.media.length === 0 && !h.linkType;

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}>
      <Card className="flex flex-col items-center gap-2 p-3 text-center">
        <div className="flex w-full items-center justify-between">
          {canManage ? (
            <button type="button" className="cursor-grab text-fg-subtle active:cursor-grabbing" {...attributes} {...listeners} aria-label={t('stories.drag', { defaultValue: 'Drag to reorder' })}>
              <GripVertical className="h-4 w-4" aria-hidden />
            </button>
          ) : <span />}
          <Badge tone={h.isPublished ? 'success' : 'neutral'}>
            {h.isPublished ? t('highlights.published', { defaultValue: 'Visible' }) : t('highlights.unpublished', { defaultValue: 'Hidden' })}
          </Badge>
        </div>

        <button type="button" onClick={onEdit} className="grid h-24 w-24 place-items-center overflow-hidden rounded-full border border-border bg-bg-subtle" aria-label={h.title}>
          {coverUrl ? <img src={coverUrl} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-7 w-7 text-fg-muted" aria-hidden />}
        </button>

        <div className="w-full">
          <div className="truncate font-semibold">{h.title}</div>
          <div className="mt-0.5 flex items-center justify-center gap-2 text-xs text-fg-muted">
            <span>{t('highlights.mediaCount', { n: h.media.length, defaultValue: 'Pictures: {{n}}' })}</span>
            {h.linkType && <Link2 className="h-3.5 w-3.5" aria-hidden />}
          </div>
          {isEmpty && (
            <div className="mt-1 text-xs text-warning">{t('highlights.emptyWarning', { defaultValue: 'Empty — add a picture or a link' })}</div>
          )}
        </div>

        {canManage && (
          <Button variant="secondary" size="sm" onClick={onEdit} className="w-full">
            <Pencil className="h-4 w-4" aria-hidden /> {t('highlights.edit', { defaultValue: 'Edit' })}
          </Button>
        )}
      </Card>
    </div>
  );
}

/**
 * Create/edit in one dialog: title, cover, pictures and link together.
 *
 * A NEW highlight is created the moment there is a title, so the pictures have
 * somewhere to attach — uploads need a parent id, and asking the shop to press
 * Save, reopen and then upload is the flow this page had and the reason adding
 * one was tedious.
 */
function HighlightDialog({
  highlight,
  productOptions,
  categoryOptions,
  brandOptions,
  onClose,
  onSaved,
}: {
  highlight: Highlight | null;
  productOptions: ComboboxOption[];
  categoryOptions: ComboboxOption[];
  brandOptions: ComboboxOption[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const [h, setH] = useState<Highlight | null>(highlight);
  const [title, setTitle] = useState(highlight?.title ?? '');
  const [busy, setBusy] = useState(false);
  const [coverVersion, setCoverVersion] = useState(0);
  // The link picker is driven from LOCAL state, not from the saved row.
  // Choosing a type fires onChange(type, null) — there is no target yet — so a
  // handler that only persists a complete pair leaves the server row unchanged,
  // and a picker fed from that row snaps straight back to "None". The chips then
  // look dead and the target box never appears, which is exactly how this
  // shipped. Local state moves first; the row is written once there is a pair.
  const [linkType, setLinkType] = useState<StoryLinkType | null>(highlight?.linkType ?? null);
  const [linkRefId, setLinkRefId] = useState<string | null>(highlight?.linkRefId ?? null);
  const coverRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const coverUrl = useObjectUrl(h?.coverAttachmentId ? () => fetchHighlightCoverObjectUrl(h.id) : null, h?.coverAttachmentId ?? 'none', coverVersion);

  /** The row to hang uploads on — created on first need, not on Save. */
  const ensure = async (): Promise<Highlight | null> => {
    if (h) return h;
    if (!title.trim()) { toast.error(t('highlights.needTitle', { defaultValue: 'Give it a title first.' })); return null; }
    const created = await createHighlight({ title: title.trim() });
    setH(created);
    await onSaved();
    return created;
  };

  const saveTitle = async (): Promise<void> => {
    const name = title.trim();
    if (!name || !h || name === h.title) return;
    setH(await updateHighlight(h.id, { title: name }));
    await onSaved();
  };

  const uploadCover = async (f: File | null): Promise<void> => {
    if (!f) return;
    setBusy(true);
    try {
      const target = await ensure();
      if (!target) return;
      setH(await setHighlightCover(target.id, f, f.name));
      setCoverVersion((v) => v + 1);
      await onSaved();
    } catch { toast.error(tc('errors:server')); }
    finally { setBusy(false); if (coverRef.current) coverRef.current.value = ''; }
  };

  /** Multi-select upload: dropping ten pictures should cost one action. */
  const uploadMedia = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const target = await ensure();
      if (!target) return;
      // Sequential, not Promise.all: sort_order is read-then-written per upload,
      // and ten concurrent appends would race for the same position.
      for (const f of Array.from(files)) {
        await addHighlightMedia(target.id, f, f.name, f.type || 'image/jpeg');
      }
      setH(await refresh(target.id));
      await onSaved();
    } catch { toast.error(tc('errors:server')); }
    finally { setBusy(false); if (mediaRef.current) mediaRef.current.value = ''; }
  };

  const refresh = async (id: string): Promise<Highlight> => (await listHighlights()).find((x) => x.id === id) ?? h!;

  const removeMedia = async (mediaId: string): Promise<void> => {
    if (!h) return;
    await deleteHighlightMedia(h.id, mediaId);
    setH(await refresh(h.id));
    await onSaved();
  };

  const onMediaDrag = async (e: DragEndEvent): Promise<void> => {
    const { active, over } = e;
    if (!h || !over || active.id === over.id) return;
    const ids = h.media.map((m) => m.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    setH({ ...h, media: next.map((id) => h.media.find((m) => m.id === id)!) }); // optimistic
    await reorderHighlightMedia(h.id, next);
    setH(await refresh(h.id));
    await onSaved();
  };

  const onLinkChange = async (type: StoryLinkType | null, refId: string | null): Promise<void> => {
    setLinkType(type);
    setLinkRefId(refId);
    // Clearing is complete on its own; choosing a type is not — wait for the
    // target before writing, or the row would hold a type pointing at nothing.
    if (type !== null && !refId) return;
    const target = await ensure();
    if (!target) return;
    setH(type === null
      ? await updateHighlight(target.id, { linkType: null, linkRefId: null })
      : await updateHighlight(target.id, { linkType: type, linkRefId: refId }));
    await onSaved();
  };

  const togglePublish = async (): Promise<void> => {
    if (!h) return;
    setBusy(true);
    try { setH(await updateHighlight(h.id, { isPublished: !h.isPublished })); await onSaved(); }
    finally { setBusy(false); }
  };

  const onDelete = async (): Promise<void> => {
    if (!h) return;
    if (!(await confirmDialog({ message: t('highlights.confirmDelete', { defaultValue: 'Delete this highlight?' }), tone: 'danger' }))) return;
    await deleteHighlight(h.id);
    await onSaved();
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {highlight ? t('highlights.editTitle', { defaultValue: 'Highlight' }) : t('highlights.add', { defaultValue: 'New highlight' })}
          </DialogTitle>
        </DialogHeader>
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-4">
          <button
            type="button"
            onClick={() => coverRef.current?.click()}
            className="grid h-20 w-20 flex-none place-items-center overflow-hidden rounded-full border border-border bg-bg-subtle hover:border-brand"
            aria-label={t('highlights.uploadCover', { defaultValue: 'Upload cover' })}
          >
            {coverUrl ? <img src={coverUrl} alt="" className="h-full w-full object-cover" /> : <Upload className="h-6 w-6 text-fg-muted" aria-hidden />}
          </button>
          <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => void uploadCover(e.target.files?.[0] ?? null)} />
          <div className="min-w-0 flex-1">
            <FormField id="hl-title" label={t('highlights.titleField', { defaultValue: 'Title' })}>
              <Input
                id="hl-title"
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => void saveTitle()}
                maxLength={200}
                placeholder={t('highlights.titlePlaceholder', { defaultValue: 'e.g. Offers' })}
              />
            </FormField>
            <p className="mt-1 text-xs text-fg-muted">{t('highlights.coverHint', { defaultValue: 'The circle shown in your app. Tap it to upload.' })}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{t('highlights.media', { defaultValue: 'Pictures' })}</div>
            <Button variant="secondary" size="sm" loading={busy} onClick={() => mediaRef.current?.click()}>
              <ImagePlus className="h-4 w-4" aria-hidden /> {t('highlights.addMedia', { defaultValue: 'Add pictures' })}
            </Button>
          </div>
          <p className="text-xs text-fg-muted">{t('highlights.mediaHint', { defaultValue: 'These play full-screen when a customer taps the circle. Pick several at once; drag to reorder.' })}</p>
          <input ref={mediaRef} type="file" multiple accept="image/png,image/jpeg,image/webp,video/mp4" className="hidden" onChange={(e) => void uploadMedia(e.target.files)} />

          {!h || h.media.length === 0 ? (
            <button
              type="button"
              onClick={() => mediaRef.current?.click()}
              className="grid h-24 place-items-center rounded-md border border-dashed border-border text-sm text-fg-muted hover:border-brand hover:text-fg"
            >
              <span className="flex items-center gap-2"><ImagePlus className="h-4 w-4" aria-hidden /> {t('highlights.noMedia', { defaultValue: 'No pictures yet — the link below is used instead' })}</span>
            </button>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onMediaDrag(e)}>
              <SortableContext items={h.media.map((m) => m.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {h.media.map((m) => (
                    <MediaThumb key={m.id} highlightId={h.id} mediaId={m.id} onRemove={() => void removeMedia(m.id)} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <LinkTargetPicker
            productOptions={productOptions}
            categoryOptions={categoryOptions}
            brandOptions={brandOptions}
            type={linkType}
            refId={linkRefId}
            onChange={(ty, ref) => void onLinkChange(ty, ref)}
          />
          <p className="mt-1 text-xs text-fg-muted">{t('highlights.linkHint', { defaultValue: 'With no pictures, tapping the circle jumps straight here.' })}</p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          {h ? (
            <Button variant="ghost" onClick={() => void onDelete()}>
              <Trash2 className="h-4 w-4" aria-hidden /> {tc('actions.delete')}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            {h && (
              <Button variant="secondary" loading={busy} onClick={() => void togglePublish()}>
                {h.isPublished ? t('highlights.unpublish', { defaultValue: 'Hide' }) : t('highlights.publish', { defaultValue: 'Show in app' })}
              </Button>
            )}
            <Button onClick={() => { void saveTitle().then(onClose); }}>{tc('actions.done', { defaultValue: 'Done' })}</Button>
          </div>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}

function MediaThumb({ highlightId, mediaId, onRemove }: { highlightId: string; mediaId: string; onRemove: () => void }): JSX.Element {
  const { t } = useTranslation('stories');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mediaId });
  const url = useObjectUrl(() => fetchHighlightMediaObjectUrl(highlightId, mediaId), mediaId, 0);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="group relative aspect-square overflow-hidden rounded-md border border-border bg-bg-subtle"
    >
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-bg-muted" />}
      <button
        type="button"
        className="absolute inset-x-0 top-0 grid h-6 place-items-center bg-black/30 text-white opacity-0 group-hover:opacity-100"
        {...attributes}
        {...listeners}
        aria-label={t('stories.drag', { defaultValue: 'Drag to reorder' })}
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="absolute end-1 bottom-1 grid h-6 w-6 place-items-center rounded-full bg-black/50 text-white opacity-0 hover:bg-danger group-hover:opacity-100"
        aria-label={t('highlights.removeMedia', { defaultValue: 'Remove' })}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
